import api from "../api";
import {
  trackQuoteAccepted,
  trackQuoteCountered,
  trackQuoteCreated,
  trackQuoteRejected,
} from "../analytics-events";

export interface Quote {
  id: number;
  booking_id: number;
  status: "pending" | "accepted" | "rejected" | "countered" | "expired";
  amount: number;
  notes?: string;
  distance_miles?: number;
  can_counter?: boolean;
  can_accept?: boolean;
  operator_counter_count?: number;
  customer_counter_count?: number;
  driver?: {
    id: number;
    name: string;
    email?: string;
    phone?: string;
    profile_picture?: string;
    reputation_score?: number;
    total_ratings_count?: number;
    is_top_rated?: boolean;
  };
  vehicle?: {
    id: number;
    registration: string;
    make?: string;
    model?: string;
    category?: string;
  };
  booking?: {
    id: number;
    service_type: string;
    from_address: string;
    to_address?: string;
    status: string;
  };
  expires_at?: string;
  accepted_at?: string;
  rejected_at?: string;
  parent_quote_id?: number;
  counteroffers?: Quote[];
  created_at: string;
}

export interface CreateQuoteData {
  booking_id: number;
  vehicle_id: number;
  amount: number;
  notes?: string;
}

class QuoteService {
  async getQuotes(params?: {
    booking_id?: number;
    status?: string;
  }): Promise<{ data: Quote[]; meta: any }> {
    const response = await api.get("/quotes", { params });
    return response.data;
  }

  async getQuote(id: number): Promise<Quote> {
    const response = await api.get(`/quotes/${id}`);
    return response.data.data;
  }

  async createQuote(data: CreateQuoteData): Promise<Quote> {
    const response = await api.post("/quotes", data);
    const quote = response.data.data;
    trackQuoteCreated(quote);
    return quote;
  }

  async acceptQuote(id: number): Promise<Quote> {
    const response = await api.post(`/quotes/${id}/accept`);
    const quote = response.data.data;
    trackQuoteAccepted(quote);
    return quote;
  }

  async rejectQuote(id: number): Promise<Quote> {
    const response = await api.post(`/quotes/${id}/reject`);
    const quote = response.data.data;
    trackQuoteRejected(id, quote.booking_id);
    return quote;
  }

  async counterQuote(
    id: number,
    amount: number,
    notes?: string
  ): Promise<Quote> {
    const response = await api.post(`/quotes/${id}/counter`, {
      amount,
      notes,
    });
    const quote = response.data.data;
    trackQuoteCountered(id, amount);
    return quote;
  }
}

const quoteService = new QuoteService();

export default quoteService;
export const {
  getQuotes,
  getQuote,
  createQuote,
  acceptQuote,
  rejectQuote,
  counterQuote,
} = quoteService;
